import {Component, computed, DestroyRef, effect, inject, signal} from '@angular/core';
import {AdminPanelFilterService} from "../../service/admin-panel-filter.service";
import {WidgetColorType} from "../../../shared/components/widget-info/widget-info.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  AdminPanelFilterModalComponent
} from "../../components/admin-panel-filter-modal/admin-panel-filter-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {FinanceDashboardChartPresentationType} from "../../enum/FinanceDashboardChartType";
import moment from "moment";

@Component({
  selector: 'app-admin-panel',
  templateUrl: './admin-panel.component.html',
  styleUrls: ['./admin-panel.component.scss'],
  standalone: false,
})
export class AdminPanelComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly adminPanelFilterService = inject(AdminPanelFilterService);

  protected readonly summary = computed(() => {
    const response = this.adminPanelFilterService.errorLogsSummaryResource.value();
    return [
      {
        title: 'Total Invoices Processed',
        text: `${response?.totalInvoicesProcessed ?? 0}`,
        overLine: '',
        icon: 'check',
        color: 'success' as WidgetColorType,
      },
      {
        title: 'Total Invoices Failed',
        text: `${response?.totalInvoicesFailed ?? 0}`,
        overLine: '',
        icon: 'close',
        color: 'danger' as WidgetColorType,
      },
      {
        title: 'Max Release Date For Connexpay Funds',
        text: response?.maxReleaseDateForCxpDailyDetails ? moment(response?.maxReleaseDateForCxpDailyDetails).format('MM/DD/YYYY') : '',
        overLine: '',
        icon: 'date_range',
        color: 'info' as WidgetColorType,
      },
      {
        title: 'Max Release Date For Connexpay Daily Summary',
        text: response?.maxReleaseDateForCxpDailySummary ? moment(response?.maxReleaseDateForCxpDailySummary).format('MM/DD/YYYY') : '',
        overLine: '',
        icon: 'date_range',
        color: 'info' as WidgetColorType,
      },
    ];
  });

  protected readonly appInsightRequestsChartData = computed(() => {
    const response = this.adminPanelFilterService.appInsightRequestsResource.value();
    const totalRequest = response?.totalRequests ?? 0;
    const failedRequest = response?.failedRequests ?? 0;
    const successfulRequest = response?.succesfullRequests ?? 0;
    const totalExceptions = response?.totalExceptions ?? 0;
    const items = [
      { label: 'Failed Requests', amount: failedRequest },
      { label: 'Successful Requests', amount: successfulRequest },
      { label: 'Total Exceptions', amount: totalExceptions },
    ];
    const filtered = items
      .map(item => ({
        ...item,
        percentage: totalRequest > 0 ? ((item.amount / totalRequest) * 100) : 0,
      }))
      .filter(item => item.amount > 0 || item.percentage > 0);
    return {
      labels: filtered.map(i => i.label),
      amount: filtered.map(i => i.amount),
      percentage: filtered.map(i => i.percentage.toFixed(3)),
    };
  });
  protected readonly appInsightRequestsPresentation = signal(FinanceDashboardChartPresentationType.Amount);
  protected readonly appInsightRequestsSubtitle = computed(() => this.appInsightRequestsPresentation() === FinanceDashboardChartPresentationType.Amount ? 'amount' : 'percentage');

  protected readonly appInsightQueriesChartData = computed(() => {
    const response = this.adminPanelFilterService.appInsightQueriesResource.value();
    return {
      labels: response?.map(r => r.operationName) ?? [],
      amount: response?.map(r => r.count) ?? [],
      percentage: [],
    };
  });
  protected readonly appInsightQueriesPresentation = signal(FinanceDashboardChartPresentationType.Amount);

  constructor() {
    effect(() => {
      const open = this.adminPanelFilterService.toggleModal();
      if (!open) return;
      this.openFilters();
    });
  }

  private openFilters() {
    const dialog = this.dialog.open(AdminPanelFilterModalComponent);
    dialog.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.adminPanelFilterService.toggleModal.set(false);
      });
  }

  protected toggleAppInsightRequestsPresentation(type: number) {
    this.appInsightRequestsPresentation.set(type === 0 ? FinanceDashboardChartPresentationType.Amount : FinanceDashboardChartPresentationType.Percentage);
  }
}
